import React from 'react';

const PRIVACY_CSS = `
  .legal-page a { color: #38bdf8; text-decoration: none; }
  .legal-page a:hover { text-decoration: underline; }
  .legal-section { animation: fadeUp 0.4s ease both; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
`;

export default function PrivacyPage({ onBack }) {
  return (
    <>
      <style>{PRIVACY_CSS}</style>
      <div className="legal-page" style={s.page}>
        <div style={s.orb1} /><div style={s.orb2} />

        {/* Header */}
        <div style={s.header}>
          <button onClick={onBack} style={s.backBtn}>← Back</button>
          <div style={s.logo}>💧 <span style={s.logoText}>DailyDroplet</span></div>
          <div style={{ width: 60 }} />
        </div>

        <div style={s.body}>
          <div style={s.heroBox}>
            <h1 style={s.heroTitle}>Privacy Policy</h1>
            <p style={s.heroSub}>Last updated: April 13, 2026</p>
          </div>

          <div style={s.summaryBar}>
            <span style={s.summaryIcon}>🔒</span>
            <span style={s.summaryText}><strong style={{ color: '#f1f5f9' }}>The short version:</strong> We collect only your phone number and preferences to deliver your digest. We never sell your data. You can delete your account anytime.</span>
          </div>

          <div style={s.card}>

            <Section title="1. Who We Are">
              <p>DailyDroplet ("we", "us", "our") operates the DailyDroplet SMS digest service. This Privacy Policy explains how we collect, use, and protect your information when you subscribe to and use our Service.</p>
              <p>Contact: <a href="mailto:privacy@dailydroplet.com">privacy@dailydroplet.com</a></p>
            </Section>

            <Section title="2. Information We Collect">
              <p><strong style={{ color: '#f1f5f9' }}>Information you provide:</strong></p>
              <ul>
                <li><strong>Phone number</strong> — required to send your digest via SMS and to verify your identity</li>
                <li><strong>Name</strong> — optional, used to personalize your greeting</li>
                <li><strong>Topics & platform preferences</strong> — used to curate your digest content</li>
                <li><strong>Delivery time & timezone</strong> — used to send your digest at your chosen local time</li>
              </ul>
              <p><strong style={{ color: '#f1f5f9' }}>Information we do not collect:</strong></p>
              <ul>
                <li>Email address</li>
                <li>Payment information (the Service is free)</li>
                <li>Location data beyond your chosen timezone</li>
                <li>Browsing history or link-click tracking</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <p>We use your information solely to operate the Service:</p>
              <ul>
                <li>Send you daily SMS digest messages at your scheduled time</li>
                <li>Send one-time verification codes when you sign up or access settings</li>
                <li>Personalize your digest with your name and topic preferences</li>
                <li>Deliver your digest from sources you selected</li>
              </ul>
              <p>We do not use your information for advertising, marketing to third parties, or any purpose unrelated to delivering your digest.</p>
            </Section>

            <Section title="4. SMS & Carrier Data">
              <p>SMS messages are delivered via Twilio, our SMS provider. When you send or receive messages, your phone number and message content are processed by Twilio in accordance with their <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              <p>Your wireless carrier may collect data about your text messaging usage in accordance with their own privacy policies. We have no control over carrier data practices.</p>
            </Section>

            <Section title="5. Data Storage">
              <p>Your account data (phone number, name, preferences) is stored in Supabase, a cloud database provider. Data is stored in secure, encrypted databases. We retain your data only for as long as you maintain an active subscription.</p>
              <p>Content cache data (article titles and URLs used to build your digest) is stored temporarily and overwritten regularly. We do not track which links you clicked.</p>
            </Section>

            <Section title="6. Data Sharing">
              <p>We do not sell, rent, or trade your personal information to third parties. We share your data only with:</p>
              <ul>
                <li><strong>Twilio</strong> — to deliver SMS messages to your phone number</li>
                <li><strong>Supabase</strong> — to store your account and preferences</li>
                <li>Law enforcement or government bodies when required by law</li>
              </ul>
              <p>All third-party service providers are contractually bound to protect your data and use it only for the purposes we specify.</p>
            </Section>

            <Section title="7. Your Rights & Choices">
              <p>You have the following rights regarding your data:</p>
              <ul>
                <li><strong>Access:</strong> Log into the Manage page to view all data we hold about you</li>
                <li><strong>Correction:</strong> Update your name, topics, platforms, and delivery time in the Manage page</li>
                <li><strong>Deletion:</strong> Delete your account and all associated data via the Manage page → Account → Delete account. Alternatively, reply <strong>STOP</strong> to any message to unsubscribe (this removes your account)</li>
                <li><strong>Opt-out of SMS:</strong> Reply STOP to any message at any time</li>
              </ul>
              <p>After account deletion, all personal data is permanently removed from our systems within 30 days, except where retention is required by law.</p>
            </Section>

            <Section title="8. Data Security">
              <p>We implement industry-standard security measures including encrypted data storage, HTTPS for all web traffic, and token-based authentication for account access. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.</p>
            </Section>

            <Section title="9. Children's Privacy">
              <p>The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will delete it promptly.</p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>We may update this Privacy Policy from time to time. We will notify subscribers of material changes via SMS or by posting the updated policy on this page with a new effective date. Continued use of the Service after changes constitutes acceptance of the updated policy.</p>
            </Section>

            <Section title="11. Contact Us">
              <p>For privacy-related questions, requests, or concerns, contact us at:</p>
              <p><a href="mailto:privacy@dailydroplet.com">privacy@dailydroplet.com</a></p>
            </Section>

          </div>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="legal-section" style={s.section}>
      <h2 style={s.sectionTitle}>{title}</h2>
      <div style={s.sectionBody}>{children}</div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: '#060d1f',
    fontFamily: "'Inter', system-ui, sans-serif", color: '#94a3b8',
    position: 'relative',
  },
  orb1: { position: 'fixed', top: '5%', left: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle,rgba(14,165,233,0.07) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  orb2: { position: 'fixed', bottom: '10%', right: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(99,102,241,0.06) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px', borderBottom: '1px solid #0d1829',
    background: 'rgba(6,13,31,0.9)', backdropFilter: 'blur(16px)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  backBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 500 },
  logo: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20, fontWeight: 800, color: '#f8fafc' },
  logoText: { background: 'linear-gradient(90deg, #38bdf8, #818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  body: { maxWidth: 760, margin: '0 auto', padding: 'clamp(32px,5vw,56px) clamp(16px,4vw,32px) 80px', position: 'relative', zIndex: 1 },
  heroBox: { marginBottom: 24, textAlign: 'center' },
  heroTitle: { fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: 800, color: '#f8fafc', marginBottom: 8 },
  heroSub: { fontSize: 14, color: '#334155' },
  summaryBar: {
    background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.15)',
    borderRadius: 14, padding: '14px 20px', marginBottom: 32,
    display: 'flex', alignItems: 'flex-start', gap: 12,
  },
  summaryIcon: { fontSize: 20, flexShrink: 0, marginTop: 2 },
  summaryText: { fontSize: 14, color: '#64748b', lineHeight: 1.7 },
  card: { background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b', borderRadius: 20, padding: 'clamp(24px,4vw,40px)', display: 'flex', flexDirection: 'column', gap: 0 },
  section: { paddingBottom: 32, marginBottom: 32, borderBottom: '1px solid #0d1829' },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 14 },
  sectionBody: { display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, lineHeight: 1.8, color: '#64748b' },
};
